import { useState } from "react";
import {
  Activity,
  Brain,
  Calendar,
  ImageDown,
  Loader2,
  Play,
  RefreshCw,
  Sparkles,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/admin-api";

function describeError(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Operation failed"
  );
}

/**
 * Wraps an async op so the button shows a spinner, the toast shows the
 * server's "message" on success, and errors get toasted with the message
 * the API returned (including 502 scraper_unreachable, etc.).
 */
function useOpRunner() {
  const [runningKey, setRunningKey] = useState(null);
  const run = async (key, fn, { successPrefix } = {}) => {
    if (runningKey) return;
    setRunningKey(key);
    const t = toast.loading(`${successPrefix || "Working"}…`);
    try {
      const data = await fn();
      const msg = data?.message || `${successPrefix || "Done"}.`;
      toast.success(msg, { id: t, duration: 6000 });
      return data;
    } catch (err) {
      toast.error(describeError(err), { id: t, duration: 8000 });
      throw err;
    } finally {
      setRunningKey(null);
    }
  };
  return { runningKey, run };
}

function OpCard({
  icon: Icon,
  title,
  description,
  children,
  warning = false,
}) {
  return (
    <Card className={warning ? "border-amber-500/40" : ""}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-primary" />}
          {title}
        </CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}

function ConfirmButton({
  children,
  onConfirm,
  disabled,
  running,
  title = "Are you sure?",
  body,
  destructive = false,
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant={destructive ? "destructive" : "default"}
          disabled={disabled}
        >
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {children}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Run</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminOperations() {
  const { runningKey, run } = useOpRunner();
  const [storeId, setStoreId] = useState("");
  const [competitorId, setCompetitorId] = useState("");
  const [classifyCompetitorId, setClassifyCompetitorId] = useState("");
  const [layerDCompetitorId, setLayerDCompetitorId] = useState("");
  const [bulkRescoreCompetitorId, setBulkRescoreCompetitorId] = useState("");
  const [backfillStoreId, setBackfillStoreId] = useState("");
  const [backfillCompetitorId, setBackfillCompetitorId] = useState("");
  const [backfillLimit, setBackfillLimit] = useState(25);

  const isRunning = (key) => runningKey === key;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold">Operations</h1>
        <p className="text-sm text-muted-foreground">
          Manual triggers for sync jobs, classification, layer-D rescoring, and
          Meta image backfill. All actions are logged in <code>background_jobs</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* ----------------------------- Sync ops ----------------------------- */}
        <OpCard
          icon={RefreshCw}
          title="Refresh all stores"
          description="Re-syncs every watched competitor for every active store. One Inngest job per competitor."
          warning
        >
          <ConfirmButton
            running={isRunning("syncAll")}
            disabled={!!runningKey}
            title="Refresh every store?"
            body="This fires one competitor/sync.requested per watched competitor across every active store. Can be expensive (SerpAPI/OpenAI) if you run it on a populated DB."
            destructive
            onConfirm={() =>
              run("syncAll", () => adminApi.ops.syncAllStores(), {
                successPrefix: "Refreshing all stores",
              })
            }
          >
            Run
          </ConfirmButton>
        </OpCard>

        <OpCard
          icon={RefreshCw}
          title="Refresh one store"
          description="Re-syncs every watched competitor for a single store."
        >
          <div className="space-y-1.5">
            <Label htmlFor="op-store-id" className="text-xs">Store ID</Label>
            <Input
              id="op-store-id"
              type="number"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              placeholder="e.g. 1"
              className="h-9"
            />
          </div>
          <Button
            size="sm"
            disabled={!storeId || isRunning("syncStore")}
            onClick={() =>
              run("syncStore", () => adminApi.ops.syncStore(parseInt(storeId, 10)), {
                successPrefix: `Refreshing store ${storeId}`,
              })
            }
          >
            {isRunning("syncStore") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </OpCard>

        <OpCard
          icon={RefreshCw}
          title="Refresh one competitor"
          description="Full sync (products + ads + intelligence) for a single competitor."
        >
          <div className="space-y-1.5">
            <Label htmlFor="op-competitor-id" className="text-xs">Competitor ID</Label>
            <Input
              id="op-competitor-id"
              type="number"
              value={competitorId}
              onChange={(e) => setCompetitorId(e.target.value)}
              placeholder="e.g. 42"
              className="h-9"
            />
          </div>
          <Button
            size="sm"
            disabled={!competitorId || isRunning("syncCompetitor")}
            onClick={() =>
              run("syncCompetitor", () => adminApi.ops.syncCompetitor(parseInt(competitorId, 10)), {
                successPrefix: `Refreshing competitor ${competitorId}`,
              })
            }
          >
            {isRunning("syncCompetitor") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </OpCard>

        <OpCard
          icon={Calendar}
          title="Daily full sync (manual)"
          description="Sends an admin/daily-full-sync.manual event. The cron function still runs at 02:00 UTC."
        >
          <Button
            size="sm"
            disabled={isRunning("dailyFullSync")}
            onClick={() =>
              run("dailyFullSync", () => adminApi.ops.dailyFullSync(), {
                successPrefix: "Triggering daily full sync",
              })
            }
          >
            {isRunning("dailyFullSync") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Send event
          </Button>
        </OpCard>

        {/* --------------------------- Intelligence --------------------------- */}
        <OpCard
          icon={Brain}
          title="Classify ads (Layer B)"
          description="Triggers OpenAI-backed Layer B classification. Leave fields blank to fan out to every watched competitor."
          warning
        >
          <div className="space-y-1.5">
            <Label htmlFor="op-classify-competitor" className="text-xs">Competitor ID (optional)</Label>
            <Input
              id="op-classify-competitor"
              type="number"
              value={classifyCompetitorId}
              onChange={(e) => setClassifyCompetitorId(e.target.value)}
              placeholder="leave blank for all"
              className="h-9"
            />
          </div>
          <ConfirmButton
            running={isRunning("classify")}
            disabled={!!runningKey}
            title={classifyCompetitorId ? "Classify ads for this competitor?" : "Classify ads for ALL watched competitors?"}
            body="Each ad costs an OpenAI call. Cost grows linearly with #ads × #competitors."
            destructive={!classifyCompetitorId}
            onConfirm={() =>
              run(
                "classify",
                () =>
                  adminApi.ops.classifyAds(
                    classifyCompetitorId
                      ? { competitorId: parseInt(classifyCompetitorId, 10) }
                      : {},
                  ),
                { successPrefix: "Queueing classification" },
              )
            }
          >
            Run
          </ConfirmButton>
        </OpCard>

        <OpCard
          icon={Sparkles}
          title="Backfill Layer D (stale)"
          description="Re-scores ads where layerD.layerDStale === true. Up to 50 rows per run."
        >
          <div className="space-y-1.5">
            <Label htmlFor="op-layerd-competitor" className="text-xs">Competitor ID (optional)</Label>
            <Input
              id="op-layerd-competitor"
              type="number"
              value={layerDCompetitorId}
              onChange={(e) => setLayerDCompetitorId(e.target.value)}
              placeholder="leave blank for global"
              className="h-9"
            />
          </div>
          <Button
            size="sm"
            disabled={isRunning("backfillLayerD")}
            onClick={() =>
              run(
                "backfillLayerD",
                () =>
                  adminApi.ops.backfillLayerD(
                    layerDCompetitorId ? parseInt(layerDCompetitorId, 10) : null,
                  ),
                { successPrefix: "Queueing Layer D backfill" },
              )
            }
          >
            {isRunning("backfillLayerD") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </OpCard>

        <OpCard
          icon={Sparkles}
          title="Bulk re-score Layer D (V1)"
          description="Idempotent rescore of every watched competitor. Run after deploying V1 or bumping policy/scorer/taxonomy versions."
          warning
        >
          <div className="space-y-1.5">
            <Label htmlFor="op-bulk-rescore-competitor" className="text-xs">Competitor ID (optional)</Label>
            <Input
              id="op-bulk-rescore-competitor"
              type="number"
              value={bulkRescoreCompetitorId}
              onChange={(e) => setBulkRescoreCompetitorId(e.target.value)}
              placeholder="leave blank for all watched"
              className="h-9"
            />
          </div>
          <ConfirmButton
            running={isRunning("bulkRescore")}
            disabled={!!runningKey}
            title={bulkRescoreCompetitorId ? "Bulk rescore this competitor?" : "Bulk rescore ALL watched competitors?"}
            body="Idempotent. Safe to re-run, but touches every ad with layer-B classification across the selected scope."
            onConfirm={() =>
              run(
                "bulkRescore",
                () =>
                  adminApi.ops.bulkRescoreLayerD(
                    bulkRescoreCompetitorId ? parseInt(bulkRescoreCompetitorId, 10) : null,
                  ),
                { successPrefix: "Queueing bulk rescore" },
              )
            }
          >
            Run
          </ConfirmButton>
        </OpCard>

        {/* ------------------------------ Scraper ----------------------------- */}
        <OpCard
          icon={Activity}
          title="Scraper health"
          description="Probe the Meta snapshot scraper sidecar (SCRAPER_URL)."
        >
          <Button
            size="sm"
            variant="outline"
            disabled={isRunning("scraperProbe")}
            onClick={() =>
              run("scraperProbe", () => adminApi.ops.scraperProbe(), {
                successPrefix: "Pinging scraper",
              })
            }
          >
            {isRunning("scraperProbe") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
            Probe
          </Button>
        </OpCard>

        <OpCard
          icon={ImageDown}
          title="Backfill Meta images"
          description="Re-runs the snapshot scraper for active Meta ads with no imageUrl. Bounded to limit per request — for big jobs use the CLI."
          warning
        >
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="bm-store" className="text-xs">Store ID</Label>
              <Input
                id="bm-store"
                type="number"
                value={backfillStoreId}
                onChange={(e) => setBackfillStoreId(e.target.value)}
                placeholder="optional"
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bm-comp" className="text-xs">Competitor ID</Label>
              <Input
                id="bm-comp"
                type="number"
                value={backfillCompetitorId}
                onChange={(e) => setBackfillCompetitorId(e.target.value)}
                placeholder="optional"
                className="h-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bm-limit" className="text-xs">Limit (max 200)</Label>
            <Input
              id="bm-limit"
              type="number"
              min={1}
              max={200}
              value={backfillLimit}
              onChange={(e) => setBackfillLimit(parseInt(e.target.value, 10) || 25)}
              className="h-9"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={isRunning("backfillImages")}
              onClick={() =>
                run(
                  "backfillImages",
                  () =>
                    adminApi.ops.backfillMetaImages({
                      storeId: backfillStoreId ? parseInt(backfillStoreId, 10) : null,
                      competitorId: backfillCompetitorId ? parseInt(backfillCompetitorId, 10) : null,
                      limit: backfillLimit,
                      dryRun: true,
                    }),
                  { successPrefix: "Dry-run" },
                )
              }
            >
              {isRunning("backfillImages") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              Dry-run
            </Button>
            <ConfirmButton
              running={isRunning("backfillImages")}
              disabled={!!runningKey}
              title="Backfill Meta images?"
              body={`Will scrape up to ${backfillLimit} Meta ads using the live scraper at SCRAPER_URL. Runs synchronously — UI may take a while if you set a high limit.`}
              onConfirm={() =>
                run(
                  "backfillImages",
                  () =>
                    adminApi.ops.backfillMetaImages({
                      storeId: backfillStoreId ? parseInt(backfillStoreId, 10) : null,
                      competitorId: backfillCompetitorId ? parseInt(backfillCompetitorId, 10) : null,
                      limit: backfillLimit,
                      dryRun: false,
                    }),
                  { successPrefix: "Backfilling Meta images" },
                )
              }
            >
              Run
            </ConfirmButton>
          </div>
        </OpCard>
      </div>
    </div>
  );
}
